// src/clients/PlayerAudioEngine.js
/**
 * PlayerAudioEngine.js
 * 
 * Modular Web Audio API engine for CATSINO Hub players.
 * This class handles all synthesis, modulation, and audio lifecycle management.
 * 
 * AUDIO GRAPH STRUCTURE:
 * [Oscillator] -> [BiquadFilter] -> [GainNode] -> [Destination]
 *      |               |              |             |
 *   (Pitch)        (Timbre)        (Envelope)    (Output)
 * 
 * FEATURES:
 * - Pentatonic Join Tone: Unique note per player index.
 * - Mechanical Ticking: Event-driven discrete blips triggered by scroll distance.
 * - Reactive Parameters: Frequency, Filter Cutoff, and Gain modulated by scroll speed/position.
 */

class PlayerAudioEngine {
    constructor() {
        this.audioContext = null;
        this.oscillator = null;
        this.gainNode = null;
        this.filterNode = null;
        this.stereoPanner = null; // PHASE 1: Stereo Panning
        this.isInitialized = false;

        // Configuration / Constants
        this.SCALE_NOTES = [261.63, 293.66, 329.63, 392.00, 440.00]; // C4 Pentatonic
        this.AUDIO_TICK_THRESHOLD = 60; // Pixels of movement per tick

        // Internal State
        this.lastAudioTriggerScroll = 0;
    }

    /**
     * Initialize the Web Audio API context and nodes.
     * Should be called on client start (non-emulated).
     */
    init() {
        if (this.isInitialized) return;

        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // [1] SOURCE: Persistent Sine Oscillator
            this.oscillator = this.audioContext.createOscillator();
            this.oscillator.type = 'sine';

            // [2] TIMBRE: Lowpass Filter
            this.filterNode = this.audioContext.createBiquadFilter();
            this.filterNode.type = 'lowpass';
            this.filterNode.frequency.value = 2000;
            this.filterNode.Q.value = 1;

            // [3] AMPLITUDE: Master Gain Node
            this.gainNode = this.audioContext.createGain();
            this.gainNode.gain.value = 0; // Start silent

            // [4] SPATIALIZATION: Stereo Panner (PHASE 1)
            this.stereoPanner = this.audioContext.createStereoPanner();
            this.stereoPanner.pan.value = 0; // Start centered

            // ROUTING: Oscillator → Filter → Gain → Panner → Destination
            this.oscillator.connect(this.filterNode);
            this.filterNode.connect(this.gainNode);
            this.gainNode.connect(this.stereoPanner);
            this.stereoPanner.connect(this.audioContext.destination);

            this.oscillator.start();
            this.isInitialized = true;
            console.log('[PlayerAudioEngine] Initialized with stereo panning');
        } catch (err) {
            console.error('[PlayerAudioEngine] Init Error:', err);
        }
    }

    /**
     * Resume audio context. Crucial for browser autoplay policies.
     * Call on first user interaction (touch/wheel/click).
     */
    resume() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
    }

    /**
     * Play a celebratory blip when a player joins.
     * Frequency is mapped to a notes from a pentatonic scale.
     */
    playJoinTone(playerIndex) {
        if (!this.isInitialized) return;

        const noteIndex = playerIndex % this.SCALE_NOTES.length;
        const frequency = this.SCALE_NOTES[noteIndex];
        const now = this.audioContext.currentTime;

        this.oscillator.frequency.setValueAtTime(frequency, now);

        this.gainNode.gain.cancelScheduledValues(now);
        this.gainNode.gain.setValueAtTime(0.0001, now);
        this.gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
        this.gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

        console.log(`[PlayerAudioEngine] Join tone for ID ${playerIndex} -> ${frequency}Hz`);
    }

    /**
     * Trigger percussive 'ticks' based on scroll activity.
     * @param {number} scrollNorm - 0..1 normalized scroll position
     * @param {number} speed - 0..1 normalized scroll speed
     * @param {boolean} muted - current mute state
     * @param {number} currentScroll - absolute virtual scroll position
     */
    updateScroll(scrollNorm, speed, muted, currentScroll) {
        if (!this.isInitialized || muted) return;

        // Check if we've moved far enough to 'tick'
        const delta = Math.abs(currentScroll - this.lastAudioTriggerScroll);

        if (delta > this.AUDIO_TICK_THRESHOLD && speed > 0.05) {
            this.lastAudioTriggerScroll = currentScroll;
            const now = this.audioContext.currentTime;

            // POSITION -> PITCH MAPPING (150Hz - 600Hz)
            const frequency = 150 + (scrollNorm * 450);
            this.oscillator.frequency.setValueAtTime(frequency, now);

            // SPEED -> VOLUME/TIMBRE MAPPING
            const volume = Math.min(speed * 0.25, 0.25);
            const filterFreq = 1000 + (speed * 3000); // Higher speed = Brighter tone

            this.gainNode.gain.cancelScheduledValues(now);
            this.gainNode.gain.setValueAtTime(0.0001, now);
            this.gainNode.gain.linearRampToValueAtTime(volume, now + 0.005);
            this.gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

            this.filterNode.frequency.setValueAtTime(filterFreq, now);
        }
    }

    /**
     * Sync the trigger baseline to prevent immediate firing on load
     */
    syncScrollBaseline(val) {
        this.lastAudioTriggerScroll = val;
    }

    /**
     * Update stereo panning based on horizontal position (PHASE 1: Spatialization)
     * @param {number} xPosition - Normalized X position (0..1)
     */
    updatePan(xPosition) {
        if (!this.isInitialized || !this.stereoPanner) return;

        // Map 0..1 to -1..1 (left to right)
        const panValue = (xPosition * 2) - 1;

        // Smooth transition to avoid zipper noise
        const now = this.audioContext.currentTime;
        this.stereoPanner.pan.setTargetAtTime(panValue, now, 0.05);
    }
}

export { PlayerAudioEngine };
